import { Injectable } from '@angular/core';
import { Sprite, Texture, TextureLoader, Vector3 } from 'three';
import { SpriteData } from '@src/app/model/sprite-data';

export enum AttachmentPointKind {
  add = 'add',
  addSelected = 'addSelected',
  missing = 'missing',
  missingSelected = 'missingSelected',
  tower = 'tower',
  towerSelected = 'towerSelected',
  towerMissing = 'towerMissing',
  towerMissingSelected = 'towerMissingSelected'
}

@Injectable({
  providedIn: 'root',
})
export class AttachmentPointService {
  public static readonly ATTACHMENT_POINT_SIZES: Vector3 = new Vector3(36, 36, 1);
  public static readonly EDIT_POINT_SIZES: Vector3 = new Vector3(66, 66, 1);

  private _textureLoader: TextureLoader = new TextureLoader();

  private _allAPKinds = [
    AttachmentPointKind.add,
    AttachmentPointKind.addSelected,
    AttachmentPointKind.missing,
    AttachmentPointKind.missingSelected,
    AttachmentPointKind.tower,
    AttachmentPointKind.towerSelected,
    AttachmentPointKind.towerMissing,
    AttachmentPointKind.towerMissingSelected,
  ];

  // Important to preload all textures to make it faster to make the sprites
  private _allTextures: Map<AttachmentPointKind, Texture<HTMLImageElement>> = new Map(
    this._allAPKinds.map(apKind => [apKind, this._textureLoader.load(this.getPath(apKind))])
  );

  /**
   * Gets created texture
   */
  public getTexture(apKind: AttachmentPointKind): Texture<HTMLImageElement> {
    return this._allTextures.get(apKind);
  }

  public getPath(apKind: AttachmentPointKind): string {
    return `assets/icons/canvas/${apKind}.svg`;
  }

  /**
   * Deselect Attachment Point (or edit point)
   */
  public deselectSprite(attachmentPoint: Sprite) {
    this.resetSpriteTexture(attachmentPoint);
  }

  /**
   * Select Attachment Point (or edit point)
   */
  public selectSprite(attachmentPoint: Sprite) {
    this.resetSpriteTexture(attachmentPoint);
    this.changeSpriteTexture(attachmentPoint, SpriteData.fromSprite(attachmentPoint).selectedKind);
  }

  /**
   * change the texture of a given sprite to the provided attachmentPointKind's texture
   */
  public changeSpriteTexture(
    sprite: Sprite,
    attachmentPointKind: AttachmentPointKind,
  ): void {
    sprite.material.map = this.getTexture(attachmentPointKind);
    // Typescript thinks image.source doesn't exist, but it does, so we ignore the error.
    // @ts-ignore
    sprite.material.map.image.source = this.getPath(attachmentPointKind);
  }

  /**
   * reset a sprite texture to the one defined in spriteData.default_kind
   */
  public resetSpriteTexture(sprite: Sprite): void {
    if (SpriteData.spriteHasData(sprite)) {
      const kind: AttachmentPointKind = SpriteData.fromSprite(sprite).defaultKind;
      this.changeSpriteTexture(sprite, kind);
    }
  }
}
